/**
 * RESULTS — the death and victory screen (doc 02 §Lose, doc 08 §First-loss).
 *
 * The rule this screen exists to make true: **failure always pays**. Every
 * defeat lands here with a Pearl tally, a tower-XP tally, and — for the first
 * three losses — a gift the player did not have when the run started.
 */

import { S } from '../../content/strings.ts';
import { levelOf, toNextLevel } from '../../meta/xp.ts';
import { font } from '../../render/font/font.ts';
import { PixelSurface } from '../../render/surface.ts';
import { placeableDef } from '../../sim/defs.ts';
import { TICK_RATE } from '../../sim/const.ts';
import type { FrameOpts, MenuItem } from '../../ui/menu.ts';
import { wrapText } from '../../ui/menu.ts';
import { g, UI } from '../../ui/theme.ts';
import { drawPanel, drawStatRow, type Rect } from '../../ui/widgets.ts';
import type { App } from '../app.ts';
import type { RunResult } from '../run.ts';
import { ColonyScreen } from './colony.ts';
import { DepthScreen } from './depth.ts';
import { MenuScreen } from './menuscreen.ts';
import { TitleScreen } from './title.ts';

const DEFEAT_LINES = [
  S.results.landlordDeadDeposit,
  S.results.fishFiledComplaint,
  S.results.colonyNowArtificial,
  S.results.oceanWouldLike,
];

export class ResultsScreen extends MenuScreen {
  private readonly result: RunResult;

  constructor(app: App, result: RunResult) {
    super(app);
    this.result = result;
  }

  protected frame(): FrameOpts {
    const won = this.result.outcome === 'won';
    return {
      title: won ? S.results.shelfCleared : S.results.colonyCrushed,
      subtitle: won
        ? S.results.reefTyrantDown
        : DEFEAT_LINES[this.result.ticks % DEFEAT_LINES.length]!,
      footer: S.results.enterPicksPearls,
      stamp: `+${this.result.pearls} PEARLS THIS RUN`,
    };
  }

  protected override columnWidth(body: Rect): number {
    return Math.min(300, Math.floor(body.w * 0.45));
  }

  protected items(): MenuItem[] {
    const gift = this.result.reveal;
    return [
      {
        label: gift ? S.results.seeWhatGot : S.results.colonyCommand,
        hotkey: 'C',
        hint: gift ? gift.headline : S.results.spendPearlsBefore,
        tone: gift ? 'good' : 'ink',
      },
      { label: S.results.descendAgain, hotkey: 'D', hint: S.results.freshColonyShelf },
      { label: S.results.title, hotkey: 'T' },
    ];
  }

  protected activate(index: number): void {
    if (index === 0) this.app.go((a) => new ColonyScreen(a));
    else if (index === 1) this.app.go((a) => new DepthScreen(a));
    else this.app.go((a) => new TitleScreen(a));
  }

  protected override back(): void {
    this.app.go((a) => new TitleScreen(a));
  }

  protected override detail(s: PixelSurface, body: Rect): void {
    const r = this.result;
    const x = body.x + this.columnWidth(body) + g(6);
    const w = Math.min(300, body.x + body.w - x);
    if (w < 160) return;

    const header = font('header');
    const bodyFont = font('body');
    let y = body.y;
    header.draw(s, S.results.run, x, y, UI.ink, { caps: true, letterSpacing: 1 });
    y += header.capHeight + g(2);
    const bottom = body.y + body.h;
    const row = (label: string, value: string, tone = UI.ink): void => {
      if (y + 12 > bottom) return; // short window: clip the tail, never spill
      drawStatRow(s, { x, y, w, h: 8 }, label, value, tone);
      y += 12;
    };
    row(S.results.fightsCleared, `${r.fightsCleared} / 12`);
    row(S.results.coreLeft, `${r.coreHp} / ${r.coreMaxHp}`, r.coreHp > 0 ? UI.good : UI.critical);
    row(S.results.timeUnder, formatTicks(r.ticks));
    row(S.results.pearlsBanked, `+${r.pearls}`, UI.accent);
    row(S.results.pearlsTotal, `${this.app.profile?.pearls ?? 0}`);

    y += g(2);
    header.draw(s, S.results.towerXp, x, y, UI.ink, { caps: true, letterSpacing: 1 });
    y += header.capHeight + g(2);
    const xp = Object.entries(r.xp).sort((a, b) => b[1] - a[1]);
    if (xp.length === 0) {
      bodyFont.draw(s, S.results.noTowerLanded, x, y, UI.inkDim, { caps: true });
      y += 12;
    }
    for (const [def, gained] of xp) {
      const total = this.app.profile?.towerXp[def] ?? 0;
      const next = toNextLevel(total);
      const name = placeableDef(def)?.name ?? def.toUpperCase();
      row(`${name} L${levelOf(total)}`, next === null ? `+${gained} · MAXED` : `+${gained} · ${next} TO GO`);
    }

    const gift = this.result.reveal;
    if (gift) {
      const panel: Rect = { x, y: y + g(2), w, h: 70 };
      drawPanel(s, panel, { raised: true });
      header.draw(s, gift.headline, panel.x + g(2), panel.y + g(2), UI.good, { caps: true, letterSpacing: 1 });
      let gy = panel.y + g(2) + header.capHeight + g(2);
      for (const line of wrapText('body', gift.text, panel.w - g(4))) {
        bodyFont.draw(s, line, panel.x + g(2), gy, UI.inkDim, { caps: true });
        gy += 11;
      }
    }
  }
}

function formatTicks(ticks: number): string {
  const total = Math.round(ticks / TICK_RATE);
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}
